import { isAdult, latestAdultBirthDate } from '../../utils/agePolicy';
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, UserCircleIcon, CalendarDaysIcon, ClockIcon, CheckCircleIcon, XCircleIcon, IdentificationIcon, EnvelopeIcon, PhoneIcon, BriefcaseIcon, PencilSquareIcon, XMarkIcon, CheckIcon, MapPinIcon, ArrowPathIcon, GlobeAltIcon, QrCodeIcon } from '@heroicons/react/24/outline';
import { fetchWithAuth } from '../../services/api';
import { countries, getCountryName } from '../../utils/countries';

const AdminUserDetails = () => {
    const { t, i18n } = useTranslation();
    const { userId } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const [editFormData, setEditFormData] = useState({
        dateOfBirth: '',
        fullName: '',
        email: '',
        phone: '',
        role: '',
        accountNonExpired: true,
        accountNonLocked: true,
        credentialsNonExpired: true,
        enabled: true,
        address: '',
        city: '',
        country: '',
        maxProfiles: 1,
        defaultLanguage: 'ENGLISH'
    });
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState(null);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditFormData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleCancel = () => {
        if (user) {
            setEditFormData({
                dateOfBirth: user.dateOfBirth || '',
                fullName: user.fullName,
                email: user.email,
                phone: user.phone,
                role: user.role,
                accountNonExpired: user.accountNonExpired,
                accountNonLocked: user.accountNonLocked,
                credentialsNonExpired: user.credentialsNonExpired,
                enabled: user.enabled,
                address: user.address || '',
                city: user.city || '',
                country: user.country || '',
                maxProfiles: user.maxProfiles || 1,
                defaultLanguage: user.defaultLanguage || 'ENGLISH'
            });
        }
        setIsEditing(false);
        setSaveMessage(null);
    };

    const handleSave = async () => {
        if (editFormData.dateOfBirth && !isAdult(editFormData.dateOfBirth)) {
            setSaveMessage({ type: 'error', text: t('agePolicy.invalid') });
            return;
        }
        setIsSaving(true);
        setSaveMessage(null);
        try {
            const updatedUser = await fetchWithAuth(`/admin/users/${userId}`, {
                method: 'PUT',
                body: { ...editFormData, dateOfBirth: editFormData.dateOfBirth || null },
            });
            setUser(updatedUser);
            setEditFormData({
                dateOfBirth: updatedUser.dateOfBirth || '',
                fullName: updatedUser.fullName,
                email: updatedUser.email,
                phone: updatedUser.phone,
                role: updatedUser.role,
                accountNonExpired: updatedUser.accountNonExpired,
                accountNonLocked: updatedUser.accountNonLocked,
                credentialsNonExpired: updatedUser.credentialsNonExpired,
                enabled: updatedUser.enabled,
                address: updatedUser.address || '',
                city: updatedUser.city || '',
                country: updatedUser.country || '',
                maxProfiles: updatedUser.maxProfiles || 1,
                defaultLanguage: updatedUser.defaultLanguage || 'ENGLISH'
            });
            setIsEditing(false);
            setSaveMessage({ type: 'success', text: t('adminUserDetails.updateSuccessMessage') });
        } catch (error) {
            console.error('Error updating user:', error);
            setSaveMessage({ type: 'error', text: t('adminUserDetails.errorUpdating') });
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        if (isEditing && user) {
            setEditFormData({
                dateOfBirth: user.dateOfBirth || '',
                fullName: user.fullName || '',
                email: user.email || '',
                phone: user.phone || '',
                role: user.role || 'USER',
                accountNonExpired: user.accountNonExpired !== undefined ? user.accountNonExpired : true,
                accountNonLocked: user.accountNonLocked !== undefined ? user.accountNonLocked : true,
                credentialsNonExpired: user.credentialsNonExpired !== undefined ? user.credentialsNonExpired : true,
                enabled: user.enabled !== undefined ? user.enabled : true,
                address: user.address || '',
                city: user.city || '',
                country: user.country || '',
                maxProfiles: user.maxProfiles || 1,
                defaultLanguage: user.defaultLanguage || 'ENGLISH'
            });
        }
    }, [isEditing, user]);

    useEffect(() => {
        const fetchUserDetails = async () => {
            setIsLoading(true);
            setFetchError(null);
            try {
                const data = await fetchWithAuth(`/admin/users/${userId}`);
                setUser(data);
                setEditFormData({
                    dateOfBirth: data.dateOfBirth || '',
                fullName: data.fullName || '',
                    email: data.email || '',
                    phone: data.phone || '',
                    role: typeof data.role === 'string' ? data.role : 'USER',
                    accountNonExpired: data.accountNonExpired !== undefined ? data.accountNonExpired : true,
                    accountNonLocked: data.accountNonLocked !== undefined ? data.accountNonLocked : true,
                    credentialsNonExpired: data.credentialsNonExpired !== undefined ? data.credentialsNonExpired : true,
                    enabled: data.enabled !== undefined ? data.enabled : true,
                    address: data.address || '',
                    city: data.city || '',
                    country: data.country || '',
                    maxProfiles: data.maxProfiles || 1,
                    defaultLanguage: data.defaultLanguage || 'ENGLISH'
                });
            } catch (err) {
                console.error('Error fetching user details:', err);
                setFetchError(err.message || t('adminUserDetails.errorFetching'));
            } finally {
                setIsLoading(false);
            }
        };

        if (userId) {
            fetchUserDetails();
        }
    }, [userId, t]);

    const DetailItem = ({ icon: Icon, label, value }) => (
        <div className="flex items-center space-x-2 py-1">
            <Icon className="h-5 w-5 text-gray-500" />
            <span className="font-semibold">{label}:</span>
            <span>{String(value)}</span>
        </div>
    );

    const BooleanStatus = ({ label, value }) => (
        <div className="flex items-center space-x-2 py-1">
            {value ? <CheckCircleIcon className="h-5 w-5 text-green-500" /> : <XCircleIcon className="h-5 w-5 text-red-500" />}
            <span>{label}</span>
        </div>
    );

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <ArrowPathIcon className="animate-spin h-10 w-10 text-indigo-600" />
                <p className="ml-2 text-lg">{t('admin.loading')}</p>
            </div>
        );
    }

    if (fetchError) {
        return (
            <div className="container mx-auto p-4 text-center">
                <h1 className="text-2xl font-semibold text-red-600 mb-4">{t('adminUserDetails.errorTitle')}</h1>
                <p className="text-red-500 mb-4">{fetchError}</p>
                <button
                    onClick={() => navigate(`/${i18n.language}/admin/users`)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded inline-flex items-center"
                >
                    <ArrowLeftIcon className="h-5 w-5 mr-2" />
                    {t('adminUserDetails.backToUsers')}
                </button>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="container mx-auto p-4 text-center">
                 <h1 className="text-2xl font-semibold text-gray-700 mb-4">{t('adminUserDetails.userNotFound')}</h1>
                 <button
                    onClick={() => navigate(`/${i18n.language}/admin/users`)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded inline-flex items-center"
                >
                    <ArrowLeftIcon className="h-5 w-5 mr-2" />
                    {t('adminUserDetails.backToUsers')}
                </button>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4 bg-gray-50 min-h-screen">
            <div className="mb-6">
                <Link to={`/${i18n.language}/admin/users`} className="text-indigo-600 hover:text-indigo-800 inline-flex items-center">
                    <ArrowLeftIcon className="h-5 w-5 mr-2" />
                    {t('adminUserDetails.backToUsers')}
                </Link>
            </div>

            <div className="bg-white shadow-xl rounded-lg p-6 md:p-8">
                <div className="flex flex-col md:flex-row items-center md:items-start mb-6 md:mb-8 pb-6 border-b border-gray-200">
                    <UserCircleIcon className="h-24 w-24 md:h-32 md:w-32 text-indigo-500 mb-4 md:mb-0 md:mr-8" />
                    <div>
                        <h1 className="text-3xl md:text-4xl font-bold text-gray-800" data-testid="admin-user-label-fullName">{user.fullName}</h1>
                        <p className="text-lg text-gray-600">
                            {user?.role ? t(`roles.${user.role.toLowerCase()}`) : t('roles.user')} - ID: {user.id}
                        </p>
                    </div>
                    {!isEditing && (
                        <button
                            onClick={() => { setIsEditing(true); setSaveMessage(null); }}
                            className="ml-auto mt-4 md:mt-0 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg inline-flex items-center shadow-md hover:shadow-lg transition duration-150 ease-in-out"
                            data-testid="admin-user-details-edit-button"
                        >
                            <PencilSquareIcon className="h-5 w-5 mr-2" />
                            {t('common.edit')}
                        </button>
                    )}
                </div>

                {saveMessage && (
                    <div 
                      className={`p-4 mb-4 rounded-md text-sm ${saveMessage.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                      data-testid="admin-user-details-save-message"
                    >
                        {saveMessage.text}
                    </div>
                )}

                {isEditing ? (
                    <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div>
                                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">{t('adminUserDetails.edit.fullName')}</label>
                                <input type="text" name="fullName" id="fullName" value={editFormData.fullName} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-testid="admin-user-form-fullName" />
                            </div>
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">{t('adminUserDetails.edit.email')}</label>
                                <input type="email" name="email" id="email" value={editFormData.email} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-testid="admin-user-form-email" />
                            </div>
                            <div>
                                <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700">{t('agePolicy.dateOfBirth')}</label>
                                <input type="date" id="dateOfBirth" name="dateOfBirth" min="0001-01-01" max={latestAdultBirthDate()} value={editFormData.dateOfBirth || ''} onChange={handleInputChange} data-testid="admin-user-form-dateOfBirth" className="block w-full rounded-md border border-gray-300 px-3 py-2" />
                                <p className="text-sm text-gray-600">{t('agePolicy.adminHelp')}</p>
                                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">{t('adminUserDetails.edit.phone')}</label>
                                <input type="tel" name="phone" id="phone" value={editFormData.phone} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-testid="admin-user-form-phone" />
                            </div>
                            <div>
                                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">{t('adminUserDetails.edit.address')}</label>
                                <input type="text" name="address" id="address" value={editFormData.address} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-testid="admin-user-form-address" />
                            </div>
                            <div>
                                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">{t('adminUserDetails.edit.city')}</label>
                                <input type="text" name="city" id="city" value={editFormData.city} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-testid="admin-user-form-city" />
                            </div>
                            <div>
                                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">{t('adminUserDetails.edit.country')}</label>
                                <select name="country" id="country" value={editFormData.country} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-testid="admin-user-form-country">
                                    <option value="">{t('adminUserDetails.edit.selectCountry')}</option>
                                    {countries.map((country) => (
                                        <option key={country.code} value={country.code}>
                                            {country.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">{t('adminUserDetails.edit.role')}</label>
                                <select name="role" id="role" value={editFormData.role} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-testid="admin-user-form-role-select">
                                    <option value="USER">{t('roles.user')}</option>
                                    <option value="ADMIN">{t('roles.admin')}</option>
                                </select>
                            </div>
                            <div>
                                <label htmlFor="maxProfiles" className="block text-sm font-medium text-gray-700 mb-1">{t('adminUserDetails.edit.maxProfiles')}</label>
                                <input 
                                    type="number" 
                                    name="maxProfiles" 
                                    id="maxProfiles" 
                                    value={editFormData.maxProfiles} 
                                    onChange={handleInputChange} 
                                    min="1" 
                                    max="100"
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" 
                                    data-testid="admin-user-form-maxProfiles" 
                                />
                                <p className="mt-1 text-sm text-gray-500">{t('adminUserDetails.edit.maxProfilesHelp')}</p>
                            </div>
                            <div>
                                <label htmlFor="defaultLanguage" className="block text-sm font-medium text-gray-700 mb-1">{t('adminUserDetails.edit.defaultLanguage', 'Default Language')}</label>
                                <select name="defaultLanguage" id="defaultLanguage" value={editFormData.defaultLanguage} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-testid="admin-user-form-defaultLanguage-select">
                                    <option value="ENGLISH">English</option>
                                    <option value="MACEDONIAN">Macedonian</option>
                                    <option value="ALBANIAN">Albanian</option>
                                    <option value="TURKISH">Turkish</option>
                                    <option value="GERMAN">German</option>
                                    <option value="SERBIAN">Serbian</option>
                                    <option value="CROATIAN">Croatian</option>
                                </select>
                            </div>
                        </div>

                        <fieldset className="mb-6 border p-4 rounded-md">
                            <legend className="text-lg font-medium text-gray-800 px-2">{t('adminUserDetails.edit.accountStatusTitle')}</legend>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                                <label className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-100 cursor-pointer">
                                    <input type="checkbox" name="enabled" checked={editFormData.enabled} onChange={handleInputChange} className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" data-testid="admin-user-form-enabled-checkbox" />
                                    <span>{t('adminUserDetails.edit.enabled')}</span>
                                </label>
                                <label className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-100 cursor-pointer">
                                    <input type="checkbox" name="accountNonExpired" checked={editFormData.accountNonExpired} onChange={handleInputChange} className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" data-testid="admin-user-form-accountNonExpired-checkbox" />
                                    <span>{t('adminUserDetails.edit.accountNonExpired')}</span>
                                </label>
                                <label className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-100 cursor-pointer">
                                    <input type="checkbox" name="accountNonLocked" checked={editFormData.accountNonLocked} onChange={handleInputChange} className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" data-testid="admin-user-form-accountNonLocked-checkbox" />
                                    <span>{t('adminUserDetails.edit.accountNonLocked')}</span>
                                </label>
                                <label className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-100 cursor-pointer">
                                    <input type="checkbox" name="credentialsNonExpired" checked={editFormData.credentialsNonExpired} onChange={handleInputChange} className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" data-testid="admin-user-form-credentialsNonExpired-checkbox" />
                                    <span>{t('adminUserDetails.edit.credentialsNonExpired')}</span>
                                </label>
                            </div>
                        </fieldset>

                        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                data-testid="admin-user-details-cancel-button"
                            >
                                <XMarkIcon className="h-5 w-5 mr-1.5 inline-block align-text-bottom" />
                                {t('common.cancel')}
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving}
                                className="inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                                data-testid="admin-user-details-save-button"
                            >
                                {isSaving ? (
                                    <ArrowPathIcon className="animate-spin h-5 w-5 mr-2" />
                                ) : (
                                    <CheckIcon className="h-5 w-5 mr-1.5 inline-block align-text-bottom" />
                                )}
                                {isSaving ? t('admin.loading') : t('common.save')}
                            </button>
                        </div>
                    </form>
                ) : (
                    <div className="space-y-4">
                        <h2 className="text-2xl font-semibold text-gray-700 mb-4 border-b pb-2">{t('adminUserDetails.accountDetails')}</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-gray-700">
                            <DetailItem icon={IdentificationIcon} label={t('adminUserDetails.userId')} value={user.id} />
                            <DetailItem icon={UserCircleIcon} label={t('adminUserDetails.edit.fullName')} value={user.fullName} />
                            <DetailItem icon={EnvelopeIcon} label={t('adminUserDetails.edit.email')} value={user.email} data-testid="admin-user-label-email"/>
                            <DetailItem icon={CalendarDaysIcon} label={t('agePolicy.dateOfBirth')} value={user.dateOfBirth || '-'} />
                            <DetailItem icon={CheckCircleIcon} label={t('agePolicy.declaredAt')} value={user.ageDeclarationAcceptedAt || '-'} />
                            <DetailItem icon={PhoneIcon} label={t('adminUserDetails.edit.phone')} value={user.phone} />
                            <DetailItem icon={MapPinIcon} label={t('adminUserDetails.address')} value={user.address || t('common.notSet')} />
                            <DetailItem icon={GlobeAltIcon} label={t('adminUserDetails.city')} value={user.city || t('common.notSet')} />
                            <DetailItem icon={GlobeAltIcon} label={t('adminUserDetails.country')} value={user.country ? getCountryName(user.country) : t('common.notSet')} />
                            <DetailItem icon={GlobeAltIcon} label={t('adminUserDetails.defaultLanguage', 'Default Language')} value={user.defaultLanguage || 'ENGLISH'} />
                            <DetailItem 
                                icon={BriefcaseIcon} 
                                label={t('adminUserDetails.edit.role')} 
                                value={user?.role ? t(`roles.${user.role.toLowerCase()}`) : t('roles.user')} 
                            />
                            <DetailItem 
                                icon={UserCircleIcon} 
                                label={t('adminUserDetails.maxProfiles')} 
                                value={`${user.profiles?.length || 0} / ${user.maxProfiles || 1}`} 
                            />
                            <DetailItem icon={CalendarDaysIcon} label={t('adminUserDetails.createdAt')} value={new Date(user.createdAt).toLocaleString()} />
                            <DetailItem icon={ClockIcon} label={t('adminUserDetails.updatedAt')} value={new Date(user.updatedAt).toLocaleString()} />
                        </div>
                        
                        <h3 className="text-xl font-semibold text-gray-700 pt-4 mb-2 border-b pb-2">{t('adminUserDetails.accountStatus')}</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-gray-700">
                            <BooleanStatus label={t('adminUserDetails.edit.enabled')} value={user.enabled} />
                            <BooleanStatus label={t('adminUserDetails.edit.accountNonExpired')} value={user.accountNonExpired} />
                            <BooleanStatus label={t('adminUserDetails.edit.accountNonLocked')} value={user.accountNonLocked} />
                            <BooleanStatus label={t('adminUserDetails.edit.credentialsNonExpired')} value={user.credentialsNonExpired} />
                        </div>

                        <section data-testid="admin-user-qr-codes">
                            <h3 className="text-xl font-semibold text-gray-700 pt-4 mb-3 border-b pb-2">
                                {t('adminUserDetails.assignedQrCodes')} ({user.qrCodes?.length || 0})
                            </h3>
                            {user.qrCodes && user.qrCodes.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                                    {user.qrCodes.map((qrCode) => (
                                        <div
                                            key={qrCode.code}
                                            className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-4"
                                            data-testid={`admin-user-qr-code-${qrCode.code}`}
                                        >
                                            <div className="flex items-center gap-2 text-indigo-800">
                                                <QrCodeIcon className="h-6 w-6" />
                                                <span className="font-bold text-lg">{qrCode.code}</span>
                                            </div>
                                            <p className="mt-2 text-sm text-gray-600">
                                                <span className="font-semibold">{t('adminUserDetails.qrStatus')}:</span>{' '}
                                                {qrCode.status || t('common.notSet')}
                                            </p>
                                            <p className="mt-1 text-sm text-gray-600">
                                                <span className="font-semibold">{t('adminUserDetails.linkedProfile')}:</span>{' '}
                                                {qrCode.profileName ? (
                                                    <Link
                                                        to={`/${i18n.language}/profile/${qrCode.profileStringId || qrCode.profileId}`}
                                                        className="text-indigo-600 hover:text-indigo-800 hover:underline"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {qrCode.profileName}
                                                    </Link>
                                                ) : (
                                                    t('adminUserDetails.notLinkedToProfile')
                                                )}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500 pl-5">{t('adminUserDetails.noAssignedQrCodes')}</p>
                            )}
                        </section>

                        <>
                            <h3 className="text-xl font-semibold text-gray-700 pt-4 mb-2 border-b pb-2">
                                {t('adminUserDetails.profiles')} ({user.profiles?.length || 0}/{user.maxProfiles || 1})
                            </h3>
                            {user.profiles && user.profiles.length > 0 ? (
                                <ul className="list-disc list-inside space-y-1 pl-5 text-gray-700">
                                    {user.profiles.map(profile => (
                                        <li key={profile.stringId || profile.id}>
                                            <Link 
                                                to={`/${i18n.language}/profile/${profile.stringId || profile.id}`}
                                                className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {profile.name}
                                            </Link>
                                            <span className="text-gray-500"> (ID: {profile.stringId || profile.id})</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-gray-500 pl-5">{t('adminUserDetails.noProfilesForUser')}</p>
                            )}
                        </>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminUserDetails;
